import { useState } from "react"
import { ArrowUp, FilePen, Hd, Loader2, Sparkles } from "lucide-react"
import { toast } from "sonner"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button } from "@/components/ui/primitives"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useGenerationPrice } from "@/hooks/use-generation-price"
import { useTranslation } from "@/hooks/use-translation"
import { draftDaysLeft, draftFinalizeButtonKey, type DraftFinalize } from "@/lib/seedance-draft"
import { cn } from "@/lib/utils"

export function SeedanceDraftBadge({ className }: { className?: string }) {
  const { t } = useTranslation("shot")
  const label = t("draft.badge")
  return (
    <span
      title={label}
      aria-label={label}
      className={cn(
        "pointer-events-none flex h-6 w-6 items-center justify-center rounded-sm bg-black/70 text-white",
        className,
      )}
    >
      <FilePen className="h-3.5 w-3.5" aria-hidden />
    </span>
  )
}

type SeedanceDraftFinalizeButtonProps = {
  finalize: DraftFinalize
  projectId: number | null
  busy?: boolean
  onConfirm: (resolution: string) => Promise<void>
  priceKind?: "shot_video" | "canvas_generate"
  /** segment — между кнопкой генерации и длительностью. button — отдельная кнопка с текстом. */
  appearance?: "button" | "segment"
  /** Правый край группы: после сегмента больше нет кнопок. */
  segmentEnd?: boolean
  /** Высота сегмента по соседней кнопке (в пульте генерации — во всю её высоту). */
  segmentClassName?: string
}

export function SeedanceDraftFinalizeButton({
  finalize,
  projectId,
  busy = false,
  onConfirm,
  priceKind = "shot_video",
  appearance = "button",
  segmentEnd = false,
  segmentClassName,
}: SeedanceDraftFinalizeButtonProps) {
  const { t } = useTranslation(["shot", "common"])
  const [open, setOpen] = useState(false)
  const [resolution, setResolution] = useState(finalize.resolutions[0] ?? "1080p")
  const [submitting, setSubmitting] = useState(false)
  const days = draftDaysLeft(finalize.expires_at)
  const buttonKey = draftFinalizeButtonKey(finalize)
  const price = useGenerationPrice({
    kind: priceKind,
    projectId,
    enabled: open && (finalize.duration_sec ?? 0) > 0,
    params:
      priceKind === "canvas_generate"
        ? {
            model_id: finalize.model_key,
            media_kind: "video",
            duration_sec: finalize.duration_sec,
            resolution,
            generate_audio: finalize.generate_audio,
          }
        : {
            model_key: finalize.model_key,
            resolution,
            duration_sec: finalize.duration_sec,
          },
  })

  const label = t(buttonKey === "to1080p" ? "draft.to1080p" : "draft.finalize")
  const disabled = busy || submitting || !finalize.available
  const hint = !finalize.available
    ? (finalize.message ?? t("draft.unavailable"))
    : appearance === "segment" && buttonKey === "to1080p"
      ? t("draft.segmentHint")
      : appearance === "segment"
        ? label
        : null

  const trigger =
    appearance === "segment" ? (
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={disabled}
        aria-label={hint ?? label}
        className={cn(
          "rounded-l-none border-0 bg-ui-warning px-2 text-ui-on-warning hover:bg-[var(--ui-warning-hover)]",
          segmentEnd ? "rounded-r-lg" : "rounded-r-none",
          segmentClassName,
        )}
        onClick={() => setOpen(true)}
      >
        {buttonKey === "to1080p" ? <ArrowUp className="h-3.5 w-3.5" /> : <Hd className="h-3.5 w-3.5" />}
        {buttonKey === "to1080p" ? "1080p" : label}
      </Button>
    ) : (
      <Button type="button" size="sm" variant="secondary" disabled={disabled} onClick={() => setOpen(true)}>
        {label}
      </Button>
    )

  return (
    <>
      {hint ? <ActionHintTooltip label={hint}>{trigger}</ActionHintTooltip> : trigger}
      <WorkspaceDialog
        open={open}
        onOpenChange={setOpen}
        title={t("draft.dialogTitle")}
        closeAriaLabel={t("common:close")}
        size="sm"
        nested
        footer={
          <>
            <Button type="button" variant="secondary" size="sm" className="self-start" onClick={() => setOpen(false)}>
              {t("common:cancel")}
            </Button>
            <div className="flex flex-col items-center gap-1">
              <Button
                type="button"
                size="sm"
                disabled={submitting}
                onClick={() => {
                  setSubmitting(true)
                  void onConfirm(resolution)
                    .then(() => setOpen(false))
                    .catch((err: unknown) => {
                      toast.error(err instanceof Error ? err.message : t("draft.failed"))
                    })
                    .finally(() => setSubmitting(false))
                }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {label}
              </Button>
              {price.status === "loading" ? (
                <Loader2
                  className="h-2.5 w-2.5 animate-spin text-ui-text-muted"
                  aria-label={t("common:generationPriceHint")}
                />
              ) : price.label ? (
                <span className="text-[10px] leading-tight text-ui-text-muted tabular-nums">{price.label}</span>
              ) : null}
            </div>
          </>
        }
      >
        <div className="space-y-3 px-5 py-4 text-sm text-ui-text-secondary">
          <p>{t("draft.regenerateNote")}</p>
          {finalize.resolutions.length > 1 ? (
            <SegmentedControl
              items={finalize.resolutions.map((item) => ({ id: item, label: item }))}
              value={resolution}
              onChange={setResolution}
            />
          ) : null}
          {days != null ? <p>{t("draft.daysLeft", { count: days })}</p> : null}
        </div>
      </WorkspaceDialog>
    </>
  )
}
